/**
 * Daily-cap input rules (86d3ygnz3) — parsing and validation in ONE place, away from the component.
 *
 * ## Why the draft is a string and this file exists
 *
 * The obvious shape — `useState<number>` behind a `type="number"` input — is wrong in three ways that
 * all fail SILENTLY, and all three write or fake a cap change nobody asked for:
 *
 * 1. `Number('') === 0`. Clearing the field to retype it makes the draft 0, and 0 is a legal cap that
 *    stands the reader down entirely. One backspace too many is a config change.
 * 2. A `type="number"` input in badInput state (`"12e"`, `"--"`) reports `value === ''` — the same 0,
 *    reached without the user ever seeing an empty box.
 * 3. `NaN` survives into the PUT body and `JSON.stringify` turns it into `null`, which the partial-update
 *    contract reads as "field omitted". The save reports success and changes nothing.
 *
 * So the draft is the raw string, parsed exactly once, and everything downstream — Save enablement,
 * the dirty comparison, the projection line, the PUT body — reads the parsed result.
 */

/** Server-side FluentValidation bound (`UpdateDocScanVerifierConfigRequestDtoValidator`). Keep in step. */
export const MIN_DAILY_CAP = 0;
export const MAX_DAILY_CAP = 100_000;

/**
 * Below this, a cap is small enough that ordinary retry traffic can drain it.
 *
 * Not arbitrary: the budget is consumed BEFORE the call, so every automatic retry spends one unit
 * whether or not it produces a reading. During an upstream wobble one scan can spend several units on
 * its own, and a backlog draining after a hold spends one per scan per attempt. A cap in the low
 * hundreds can therefore be gone without a single successful second reading.
 */
export const SOFT_FLOOR_DAILY_CAP = 200;

export type CapDraft =
  | { ok: true; value: number }
  | { ok: false; message: string };

/**
 * Parse the raw input. Integer only, in range, no empty, no NaN, no decimals.
 *
 * Rejects decimals rather than rounding them: a cap is a count of calls, and silently turning a typed
 * `1500.5` into something else is the same class of surprise the string draft exists to prevent.
 */
export function parseCapDraft(raw: string): CapDraft {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, message: 'Enter a number — the field can’t be left empty.' };
  if (!/^\d+$/.test(trimmed)) return { ok: false, message: 'Whole numbers only, with no decimal point or sign.' };

  const value = Number(trimmed);
  if (!Number.isSafeInteger(value)) return { ok: false, message: 'That number is too large to be a call cap.' };
  if (value < MIN_DAILY_CAP || value > MAX_DAILY_CAP) {
    return { ok: false, message: `Pick a number between ${MIN_DAILY_CAP} and ${MAX_DAILY_CAP.toLocaleString()}.` };
  }
  return { ok: true, value };
}

/**
 * Has the cap actually changed? Compares the PARSED value, never the string — otherwise `"0500"` and
 * `"500 "` read as edits, the Save button lights up for nothing, and the PUT carries a field the admin
 * never meant to touch (which is exactly how a stale dialog reverts somebody else's raise).
 */
export function isCapDirty(draft: CapDraft, current: number): boolean {
  return draft.ok && draft.value !== current;
}
